import { useMemo, useCallback, useState, useEffect } from "react";

interface SSOSession {
  access_token: string;
  user: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    org: string;
    role: string;
    tools: string[];
  };
}

export interface AppUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  profilePictureUrl?: string;
}

export interface AppAuth {
  user: AppUser | null;
  role: string | undefined;
  isLoading: boolean;
  signIn: () => void;
  signOut: () => void;
  getAccessToken: () => Promise<string>;
}

function parseJwtExp(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp ?? null;
  } catch {
    return null;
  }
}

function getSSOSession(): SSOSession | null {
  try {
    const raw = localStorage.getItem("sso_session");
    if (!raw) return null;
    const session: SSOSession = JSON.parse(raw);
    const hasIncomingToken =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).has("token");

    const exp = parseJwtExp(session.access_token);
    if (exp && exp * 1000 < Date.now()) {
      localStorage.removeItem("sso_session");
      if (!hasIncomingToken) {
        window.location.href = "https://portal.spedlogswiss.com/";
      }
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function useAppAuth(): AppAuth {
  const [sessionVersion, setSessionVersion] = useState(0);

  const ssoSession = useMemo(() => getSSOSession(), [sessionVersion]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "sso_session") setSessionVersion((v) => v + 1);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const signOut = useCallback(() => {
    const loginSource = localStorage.getItem("login_source");
    localStorage.removeItem("sso_session");
    localStorage.removeItem("login_source");
    setSessionVersion((v) => v + 1);

    if (loginSource === "spedlogswiss") {
      window.location.href = "https://portal.spedlogswiss.com/";
      return;
    }
    window.location.href = "/";
  }, []);

  const signIn = useCallback(() => {
    window.location.href = "/";
  }, []);

  const user = useMemo<AppUser | null>(() => {
    if (!ssoSession) return null;
    const u = ssoSession.user;
    return {
      id: u.id,
      firstName: u.first_name,
      lastName: u.last_name,
      email: u.email,
    };
  }, [ssoSession]);

  const getAccessToken = useCallback(
    async () => ssoSession?.access_token ?? "",
    [ssoSession],
  );

  return {
    user,
    role: ssoSession?.user.role,
    isLoading: false,
    signIn,
    signOut,
    getAccessToken,
  };
}
